import { QUESTION_TYPE } from '../../fetchSurvey/constants/questionTypes';
import type { SurveyQuestion } from '../../fetchSurvey/types/index';
import type { CfmMatrixQuestion } from '../../fetchSurvey/types/cfmMatrix';
import type { CsatQuestion } from '../../fetchSurvey/types/csat';
import type { McqQuestion } from '../../fetchSurvey/types/mcq';
import type { NpsQuestion } from '../../fetchSurvey/types/nps';
import type { RatingMatrixQuestion } from '../../fetchSurvey/types/ratingMatrix';
import type { RankOrderQuestion } from '../../fetchSurvey/types/rankOrder';
import type { SurveyAnswers, SubmitRequestBody, MatrixRowAnswers, UploadedFileAnswer } from '../types';
import { normalizeHeatmapClicks } from '../../surveyLogic/fields/heatmapRegionUtils';
import {
  formatMcqOtherSubmitText,
  getParentQuestionIdFromOptionLevelKey,
  isMcqOtherOptionSelected,
} from '../../surveyLogic/fields/mcqOtherUtils';
import {
  resolveMatrixCellSubmitValues,
  resolveMcqSubmitValues,
  resolveScalePointSubmitValue,
  resolveTextInputSubmitValue,
} from './resolveSubmitValues';
import { resolveRankOrderSubmitValues } from './resolveRankOrderSubmitValues';

type ScaleMatrixQuestion = CfmMatrixQuestion | CsatQuestion | RatingMatrixQuestion;

function isUploadedFileAnswer(item: unknown): item is UploadedFileAnswer {
  return typeof item === 'object' && item !== null && 'id' in item && 'mediaUrl' in item;
}

function getMatrixScaleColumns(question: ScaleMatrixQuestion) {
  return question.scaleColumns;
}

function collectMcqOptionLevelAnswersFromAnswers(
  answers: SurveyAnswers,
  questions: SurveyQuestion[],
  optionLevelAnswers: Record<string, string[]>
) {
  const mcqQuestions = questions.filter(
    (question): question is McqQuestion => question.type === QUESTION_TYPE.MCQ
  );

  for (const [key, raw] of Object.entries(answers)) {
    if (!key.includes('-:-') || optionLevelAnswers[key]) {
      continue;
    }

    if (typeof raw !== 'string' || raw.trim() === '') {
      continue;
    }

    const questionId = getParentQuestionIdFromOptionLevelKey(key);
    if (!questionId) {
      continue;
    }

    const question = mcqQuestions.find(item => item.id === questionId);
    if (!question) {
      continue;
    }

    const optionId = key.slice(questionId.length + 3);
    const option = question.options.find(item => item.id === optionId);
    if (!option?.otherOption) {
      continue;
    }

    const selection = answers[questionId];
    if (!isMcqOtherOptionSelected(question, selection, optionId)) {
      continue;
    }

    optionLevelAnswers[key] = [formatMcqOtherSubmitText(option.otherOption, raw.trim())];
  }
}

function appendMatrixRowAnswers(
  questionToAnswers: Record<string, string[]>,
  questionId: string,
  answerMap: MatrixRowAnswers,
  resolveRow: (rowValue: MatrixRowAnswers[string]) => string[] | null
) {
  for (const [rowId, rowValue] of Object.entries(answerMap)) {
    const resolved = resolveRow(rowValue);
    if (resolved) {
      questionToAnswers[`${questionId}_${rowId}`] = resolved;
    }
  }
}

export function buildSubmitRequestBody(
  answers: SurveyAnswers,
  questions: SurveyQuestion[]
): SubmitRequestBody {
  const questionToAnswers: Record<string, string[]> = {};
  const optionLevelAnswers: Record<string, string[]> = {};
  const quesIdVsAttachmentDetails: Record<string, UploadedFileAnswer[]> = {};

  for (const question of questions) {
    if (question.type === QUESTION_TYPE.FILE_UPLOAD) {
      quesIdVsAttachmentDetails[question.id] = [];
    }
  }

  for (const question of questions) {
    const value = answers[question.id];
    if (value === undefined || value === null) continue;

    switch (question.type) {
      case QUESTION_TYPE.TEXT_AND_MEDIA:
        break;

      case QUESTION_TYPE.FILE_UPLOAD: {
        if (Array.isArray(value) && value.every(isUploadedFileAnswer)) {
          quesIdVsAttachmentDetails[question.id] = value;
        }
        break;
      }

      case QUESTION_TYPE.CFM_MATRIX:
      case QUESTION_TYPE.CSAT_MATRIX:
      case QUESTION_TYPE.RATING_MATRIX: {
        if (typeof value === 'object' && !Array.isArray(value)) {
          const answerMap = value as MatrixRowAnswers;
          const matrixQuestion = question as ScaleMatrixQuestion;

          appendMatrixRowAnswers(questionToAnswers, question.id, answerMap, rowValue =>
            resolveMatrixCellSubmitValues(getMatrixScaleColumns(matrixQuestion), rowValue)
          );
        }
        break;
      }

      case QUESTION_TYPE.SLIDER_MATRIX: {
        if (typeof value === 'object' && !Array.isArray(value)) {
          const answerMap = value as MatrixRowAnswers;

          appendMatrixRowAnswers(questionToAnswers, question.id, answerMap, rowValue => {
            if (rowValue === undefined || rowValue === null) {
              return null;
            }

            if (typeof rowValue === 'number') {
              return [String(rowValue)];
            }

            if (typeof rowValue === 'string') {
              return [rowValue];
            }

            return null;
          });
        }
        break;
      }

      case QUESTION_TYPE.MCQ: {
        const resolved = resolveMcqSubmitValues(question as McqQuestion, value, answers);
        if (resolved) {
          questionToAnswers[question.id] = resolved.questionLabels;
          Object.assign(optionLevelAnswers, resolved.optionLevelAnswers);
        }
        break;
      }

      case QUESTION_TYPE.NPS_SCALE: {
        if (typeof value === 'number') {
          const scaleQuestion = question as NpsQuestion;
          questionToAnswers[question.id] = [
            resolveScalePointSubmitValue(scaleQuestion.options, value),
          ];
        }
        break;
      }

      case QUESTION_TYPE.TEXTFIELD: {
        const resolved = resolveTextInputSubmitValue(value);
        if (resolved) {
          questionToAnswers[question.id] = resolved;
        }
        break;
      }

      case QUESTION_TYPE.HEATMAP: {
        const clicks = normalizeHeatmapClicks(value);
        if (clicks.length > 0) {
          questionToAnswers[question.id] = [JSON.stringify(clicks)];
        }
        break;
      }

      case QUESTION_TYPE.RANK_ORDER: {
        const resolved = resolveRankOrderSubmitValues(question as RankOrderQuestion, value);
        if (resolved) {
          questionToAnswers[question.id] = resolved.orderedOptionIds;
          Object.assign(optionLevelAnswers, resolved.optionLevelAnswers);
        }
        break;
      }
    }
  }

  collectMcqOptionLevelAnswersFromAnswers(answers, questions, optionLevelAnswers);

  return {
    questionToAnswers,
    quesIdVsAttachmentDetails,
    optionLevelAnswers,
  };
}
